import Cookies from "js-cookie"
import React, { useEffect, useState } from "react"
import CookieConsent from "react-cookie-consent"
import { updateConsent } from "../../utils/consent"
import RichText from "../RichText"

interface CookieBannerProps {
  /**
   * The current language of the page
   */
  currentLanguage: "fr" | "en" | "de" | "es" | "it" | "pl" | "pt" | "jp" | "cn"
  /**
   * The text to be displayed in the cookie banner
   */
  text: string
  /**
   * An array of content items. Required to handle links in Kontent’s rich text fields
   */
  textLinks: any
  /**
   * The text to be displayed on the button
   */
  buttonText: string
  /**
   * Style overrides for the cookie banner
   */
  style: any
}

const CookieBanner = ({
  currentLanguage,
  text,
  textLinks,
  buttonText,
  style,
}: CookieBannerProps) => {
  const [cookiesAccepted, setCookiesAccepted] = useState(false)

  // When `acceptOnScroll` is true on CookieConsent, analytics will be
  // called each time the page is scrolled. This works around that by wrapping the
  // analytics function in a global variable that checks whether it has
  // been invoked by the banner
  const updateConsentByBanner = () => {
    if (!(window as any).cookieBannerHasInvokedAnalytics) {
      // @ts-ignore
      window.cookieBannerHasInvokedAnalytics = true
      updateConsent()
    }
  }

  useEffect(() => {
    // Clicking on the page triggers consent and replicates the cookie functionality handled by the cookie banner
    // when clicking “Continue” or scrolling the page
    const updateConsentOnClick = (event) => {
      // Remove once invoked so it only fires once. IE11 doesn’t support `{ once: true }` option.
      document.body.removeEventListener("click", updateConsentOnClick)
      // Get cookie consent banner element so we can avoid accepting cookies if the user clicks
      // within the cookie banner itself.
      const bannerElement = document.querySelector(
        ".cookieConsent, .CookieConsent"
      )

      // The click handler is set to only trigger once, so we remove and re-add it if the user clicks
      // within the cookie banner so analytics will be tracked once they click outside the banner
      if (bannerElement && bannerElement.contains(event.target)) {
        document.body.addEventListener("click", updateConsentOnClick)
      } else if (!Cookies.get("ecmCookiesAccepted")) {
        // Set the same cookie that is set by clicking “Continue” on the cookie banner or scrolling the page.
        Cookies.set("ecmCookiesAccepted", true, { expires: 365 })
        setCookiesAccepted(true)
        updateConsent()
      }
    }

    document.body.addEventListener("click", updateConsentOnClick)

    // Cleanup function
    return () => {
      document.body.removeEventListener("click", updateConsentOnClick)
    }
  }, [])

  return !cookiesAccepted ? (
    <CookieConsent
      cookieName="ecmCookiesAccepted"
      acceptOnScroll={true}
      buttonText={buttonText}
      expires={365}
      style={style}
      onAccept={updateConsentByBanner}
    >
      <div className="text-white [&_a:hover]:text-gray-400 [&_a]:text-red [&_p]:m-0 [&_p]:text-sm">
        <RichText
          currentLanguage={currentLanguage}
          content={text}
          links={textLinks}
        />
      </div>
    </CookieConsent>
  ) : null
}

export default CookieBanner
